import { createContext, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import theme from "@monorepo/styles/theme";
import { IAuthSliceState } from "@monorepo/types";
import debounce from "lodash.debounce";

interface IViewportContext {
  isSmMobile: boolean;
  isMobile: boolean;
  isXlDesktop: boolean;
  isDesktop: boolean;
  isThirdParty: boolean;
}

const defaultReturn = {
  isSmMobile: false,
  isMobile: false,
  isXlDesktop: false,
  isDesktop: false,
  isThirdParty: false,
};

const handleStateByViewportSize = ({
  width,
  isMobile,
  isThirdParty,
}: {
  width: number;
  isMobile: boolean;
  isThirdParty: boolean;
}) => {
  if (width < theme.breakpoints.values.sm) {
    return {
      ...defaultReturn,
      isSmMobile: true,
      isMobile,
      isThirdParty,
    };
  }

  if (width < theme.breakpoints.values.md) {
    return {
      ...defaultReturn,
      isMobile,
      isThirdParty,
    };
  }

  if (width < theme.breakpoints.values.xl) {
    return {
      ...defaultReturn,
      isXlDesktop: true,
      isDesktop: true,
      isThirdParty,
    };
  }

  return {
    ...defaultReturn,
    isDesktop: true,
    isThirdParty,
  };
};

interface ISetMinHeightAndVhProps {
  isMobile: boolean;
}

const setMinHeightAndVh = ({ isMobile }: ISetMinHeightAndVhProps) => {
  const html = document.getElementsByTagName("html")[0];
  const body = document.getElementsByTagName("body")[0];
  if (isMobile) {
    html.style.height = "100%";
    body.style.height = "100%";
  } else {
    html.style.minHeight = "unset";
  }
};

const toggleBackgroundColor = ({ token, isMobile }: { token: string; isMobile: boolean }) => {
  //adding lanscape class (background grey) only if user logged in and in landscape (only for mobile) - to fix landscape issues if user has device with env safe area paddings
  const html = document.getElementsByTagName("html")[0];
  if (isMobile && token && window.orientation !== 0) {
    html.classList.add("landscape");
  } else {
    html.classList.remove("landscape");
  }
};

export const ViewportContext = createContext({} as IViewportContext);

export const ViewportContextProvider = ({
  children,
  isThirdParty,
}: {
  children: React.ReactNode;
  isThirdParty: boolean;
}) => {
  const token = useSelector(({ authSlice }: IAuthSliceState) => authSlice.access_token);
  const isMobile = window.innerWidth < theme.breakpoints.values.md;
  const [state, updateState] = useState(
    handleStateByViewportSize({ width: window.innerWidth, isMobile, isThirdParty })
  );

  setMinHeightAndVh({ isMobile });
  toggleBackgroundColor({ token, isMobile });
  /*
    calculates correct vh height for mobile browsers
    https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
 */

  const handleWindowResize = debounce(() => {
    setMinHeightAndVh({ isMobile: window.innerWidth < theme.breakpoints.values.md });
    toggleBackgroundColor({
      token,
      isMobile: window.innerWidth < theme.breakpoints.values.md,
    });

    updateState(
      handleStateByViewportSize({
        width: window.innerWidth,
        isMobile: window.innerWidth < theme.breakpoints.values.md,
        isThirdParty,
      })
    );
  }, 10);

  useLayoutEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ViewportContext.Provider value={state}>{children}</ViewportContext.Provider>;
};
